import { useEffect, useState } from "react";
import { COMBO_WORD } from "../content/combo";
import { comboStart, comboTier, isTierStep } from "../game/combo";
import "./Combo.css";

/** How long the badge takes to fade when a combo ends. Matches badge-out in motion.css. */
const OUT_MS = 300;

interface Props {
  /** Right answers in a row (game/combo.ts comboOf). */
  count: number;
  /** The colour of the rank in play, for the top tier. */
  rankColour?: string | undefined;
}

/**
 * The combo badge: "×5" in a ring that pops on every right answer, steps up
 * a tier with a word, and fades out when the run ends. Display only.
 */
export function Combo({ count, rankColour = "var(--gold)" }: Props) {
  const showing = count >= comboStart();
  // Keep the last count for the fade-out after a slip (state adjusted during render).
  const [prev, setPrev] = useState(count);
  const [out, setOut] = useState<number | null>(null);
  if (count !== prev) {
    setPrev(count);
    setOut(!showing && prev >= comboStart() ? prev : null);
  }
  useEffect(() => {
    if (out === null) return;
    const t = setTimeout(() => setOut(null), OUT_MS);
    return () => clearTimeout(t);
  }, [out]);

  const n = showing ? count : out;
  if (n === null) return null;
  const tier = comboTier(n);
  const colour = tier?.colour === "rank" ? rankColour : (tier?.colour ?? "var(--bamboo)");
  const step = showing && isTierStep(n);
  const className =
    "combo display" + (step ? " step" : "") + (showing ? "" : " out") + (tier?.flash ? " top" : "");
  return (
    <div
      key={showing ? count : "out"}
      className={className}
      style={{ "--c": colour } as React.CSSProperties}
      aria-live="off"
    >
      <span className="visually-hidden">{COMBO_WORD} </span>×{n}
      {step && tier && (
        <span className="combo-word" aria-hidden="true">
          {tier.word}
        </span>
      )}
    </div>
  );
}

/** "Best combo: 12." for a result screen, or null when the best never reached a combo. */
export function bestComboText(best: number): string | null {
  return best >= comboStart() ? `Best ${COMBO_WORD.toLowerCase()}: ${best}.` : null;
}
